import {useRouter} from 'next/router';
import {motion} from 'framer-motion';
import {Button, Box, useDisclosure} from '@chakra-ui/react';

import {useTranslate} from '@eksab/i18n';
import {useGetMeQueryData, useMeQuery} from '@eksab/hooks';
import {AuthModal} from '@eksab/features/auth/modals/AuthModal';
import type {CoinsPackage} from '@eksab/features/store/types';

import {CoinsIcon, PackageDetails} from './';

type PackageProps = {
  pack: CoinsPackage;
  href: string;
};

export const Package = ({pack, href}: PackageProps) => {
  const {push} = useRouter();
  const authDisclosure = useDisclosure();

  const t = useTranslate();
  const {data: me} = useMeQuery();
  const isUser = useGetMeQueryData();

  const onPackageOpen = () => {
    mixpanel?.track('Button Clicked', {
      button: 'pricing.buy_now',
      package: pack.coins,
    });

    if (!me) return authDisclosure.onOpen();

    push(href);
  };

  const onAuthModalClose = () => {
    authDisclosure.onClose();

    if (isUser()) push(href);
  };

  return (
    <>
      <Box
        bg="white"
        rounded="md"
        shadow="xs"
        display="flex"
        flexDir="column"
        justifyContent="space-between"
        alignItems="center"
        gap={[0.5, 3, 4]}
        pos="relative"
        px={[4, null, 6]}
        pb={[4, null, 6]}
        pt="1"
        sx={{aspectRatio: '1'}}
        w="full"
        h="auto"
        cursor="pointer"
        onClick={onPackageOpen}
      >
        <Box flex={1} display="flex" flexDir="column" alignItems="center" justifyContent="center" gap={[1, 2, 3]}>
          <CoinsIcon coins={+pack.coins} />

          <PackageDetails pack={pack} />
        </Box>

        <Button
          w="full"
          h={{base: 'clamp(1.5rem, 8vw, 2.5rem)', md: 8, lg: 10, xl: '3.125rem'}}
          fontSize="clamp(1rem, 4.2vw, 1.125rem)"
          py="1"
        >
          {t('app.actions.buy')}
        </Button>
      </Box>

      <AuthModal {...authDisclosure} onClose={onAuthModalClose} />
    </>
  );
};

const Skeleton = () => (
  <Box
    as={motion.div}
    rounded="md"
    sx={{aspectRatio: '1'}}
    animate={{
      backgroundColor: ['#f3f3f3', '#ecebeb'],
      transition: {duration: 1, repeat: Infinity, repeatType: 'reverse'},
    }}
  />
);

Package.Skeleton = Skeleton;
